import type { Data } from '@dnd-kit/abstract';
import type { ComponentProps } from 'react';

import { useSortable } from '@dnd-kit/react/sortable';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { createContext, use } from 'react';

import type { SortableItemProps } from '.';

interface SortableItemContextProps {
  handleRef: (element: Element | null) => void;
}

const SortableItemContext = createContext<SortableItemContextProps | null>(null);

export const useSortableItem = () => {
  const context = use(SortableItemContext);
  if (!context) {
    throw new Error('useSortableItem must be used within a SortableItemContext');
  }
  return context;
};

export function SortableItem<T extends Data>(sortableElementProps: SortableItemProps<T>) {
  const { id, index, data, isDraggingClassName, className, args, ...props } = sortableElementProps;

  const {
    ref: setNodeRef,
    handleRef,
    isDragging,
  } = useSortable({
    id,
    index,
    data,
    ...args,
  });

  return (
    <SortableItemContext
      value={{
        handleRef,
      }}
    >
      <Slot
        ref={setNodeRef}
        data-dragging={isDragging ? 'true' : 'false'}
        aria-disabled={args?.disabled}
        data-index={index}
        className={cn('sortable-item cursor-pointer select-none active:cursor-grabbing aria-disabled:cursor-not-allowed', isDragging && 'opacity-50', isDragging && isDraggingClassName, className)}
        {...props}
      />
    </SortableItemContext>
  );
}

export function SortableItemHandler(sortableItemHandlerProps: ComponentProps<typeof Slot>) {
  const { className, ...props } = sortableItemHandlerProps;

  const { handleRef } = useSortableItem();

  return <Slot ref={handleRef} className={cn('sortable-item cursor-pointer select-none active:cursor-grabbing aria-disabled:cursor-not-allowed', className)} {...props} />;
}
